import { PageHeader } from '@/client/components/common/PageHeader';
import { StatCard } from '@/client/components/common/StatCard';
import { FinanceRecordDialog } from '@/client/components/finance/FinanceRecordDialog';
import { FinanceTable } from '@/client/components/finance/FinanceTable';
import { Button } from '@/client/components/ui/button';
import { Card } from '@/client/components/ui/card';
import { Input } from '@/client/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/client/components/ui/select';
import { useAdminGuard } from '@/client/hooks/useAdminGuard';
import { trpc } from '@/client/utils/trpc';
import type { RouterOutputs } from '@/client/utils/trpc';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  ArrowDownRight,
  ArrowUpRight,
  Download,
  Hash,
  type LucideIcon,
  Plus,
  Scale,
  Search,
  Wallet,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { z } from 'zod';

type FinanceRecord = RouterOutputs['finance']['listRecords'][number];

const searchSchema = z.object({
  type: z.enum(['ALL', 'INCOME', 'OUTCOME']).optional().default('ALL'),
  month: z.string().optional().default(''),
  category: z.string().optional().default(''),
});

export const Route = createFileRoute('/_app/finance/')({
  validateSearch: (s: Record<string, unknown>) => searchSchema.parse(s),
  component: FinanceLedgerPage,
});

// ── helpers ──────────────────────────────────────────────────────────────────

function currentMonthStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function currentYear(): number {
  return new Date().getFullYear();
}

function monthLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleString('en-US', { month: 'short', year: 'numeric' });
}

function monthToRange(ym: string): { date_from: string; date_to: string } | Record<string, never> {
  if (!ym) return {};
  const [y, m] = ym.split('-').map(Number);
  return {
    date_from: new Date(y, m - 1, 1).toISOString().slice(0, 10),
    date_to: new Date(y, m, 0).toISOString().slice(0, 10),
  };
}

function yearRange(year: number): { date_from: string; date_to: string } {
  return {
    date_from: `${year}-01-01`,
    date_to: `${year}-12-31`,
  };
}

function getLast13Months(): string[] {
  const now = new Date();
  return Array.from({ length: 13 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
}

function formatEur(n: number): string {
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.abs(n));
}

function exportCsv(records: FinanceRecord[]): void {
  const header = 'Date,Type,Category,Note,Amount';
  const rows = records.map((r) =>
    [
      r.date,
      r.type,
      `"${r.category.replace(/"/g, '""')}"`,
      `"${r.description.replace(/"/g, '""')}"`,
      r.type === 'INCOME' ? r.amount : -r.amount,
    ].join(','),
  );
  const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'ledger.csv';
  a.click();
  URL.revokeObjectURL(url);
}

// ── KPI card ─────────────────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  sub,
  valueClass,
  icon,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  valueClass?: string;
  icon?: LucideIcon;
  tone?: 'default' | 'primary' | 'good' | 'warn' | 'bad';
}) {
  return (
    <StatCard
      label={label}
      value={<span className={valueClass}>{value}</span>}
      hint={sub}
      icon={icon}
      tone={tone}
    />
  );
}

// ── type tab strip ────────────────────────────────────────────────────────────

const TYPE_OPTS = [
  { label: 'All', value: 'ALL' },
  { label: 'Income', value: 'INCOME' },
  { label: 'Outcome', value: 'OUTCOME' },
] as const;

type TypeFilter = (typeof TYPE_OPTS)[number]['value'];

function TypeTabs({
  value,
  onChange,
}: {
  value: TypeFilter;
  onChange: (v: TypeFilter) => void;
}) {
  return (
    <div className="flex gap-0.5 rounded-lg bg-muted p-0.5">
      {TYPE_OPTS.map((opt, i) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={[
            'px-3 py-1.5 text-sm font-medium transition-colors',
            'rounded-md',
                        value === opt.value
              ? 'bg-card text-foreground shadow-soft'
              : 'text-muted-foreground hover:text-foreground',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────

function FinanceLedgerPage() {
  useAdminGuard();

  const search = Route.useSearch();
  const navigate = useNavigate();

  const [q, setQ] = useState('');
  const [editRecord, setEditRecord] = useState<FinanceRecord | null>(null);

  const activeMonth = search.month; // '' means whole year
  const year = currentYear();
  const summaryPeriod = activeMonth || currentMonthStr();
  const [summaryYear, summaryMonth] = summaryPeriod.split('-').map(Number);

  const { data: categories = [] } = trpc.finance.listCategories.useQuery();
  const { data: records = [], isLoading } = trpc.finance.listRecords.useQuery({
    ...(activeMonth ? monthToRange(activeMonth) : yearRange(year)),
    type: search.type !== 'ALL' ? search.type : undefined,
    category: search.category || undefined,
  });
  const { data: summary } = trpc.finance.monthlySummary.useQuery({
    year: summaryYear,
    month: summaryMonth,
  });

  const filteredRecords = useMemo(() => {
    if (!q.trim()) return records;
    const lower = q.toLowerCase();
    return records.filter(
      (r) =>
        r.description.toLowerCase().includes(lower) || r.category.toLowerCase().includes(lower),
    );
  }, [records, q]);

  function setParam(key: 'type' | 'month' | 'category', val: string) {
    navigate({ to: '/finance', search: { ...search, [key]: val } });
  }

  const months = getLast13Months();
  const label = activeMonth ? monthLabel(activeMonth) : String(year);
  const balance = summary?.balance ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Ledger"
        eyebrow="Finance"
        icon={Wallet}
        actions={
          <>
            <TypeTabs value={search.type} onChange={(v) => setParam('type', v)} />
            <Button variant="outline" size="sm" onClick={() => exportCsv(filteredRecords)}>
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
            <Button size="sm" asChild>
              <Link to="/finance/new">
                <Plus className="h-4 w-4" />
                Add entry
              </Link>
            </Button>
          </>
        }
      />

      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard
          label={`Income · ${label}`}
          value={`+€${formatEur(summary?.totalIncome ?? 0)}`}
          valueClass="text-good"
          icon={ArrowUpRight}
          tone="good"
        />
        <KpiCard
          label={`Outcome · ${label}`}
          value={`–€${formatEur(summary?.totalOutcome ?? 0)}`}
          icon={ArrowDownRight}
          tone="bad"
        />
        <KpiCard
          label={`Net · ${label}`}
          value={`${balance >= 0 ? '+' : '–'}€${formatEur(balance)}`}
          valueClass={balance >= 0 ? 'text-good' : 'text-bad'}
          icon={Scale}
          tone="primary"
        />
        <KpiCard
          label={`Records · ${label}`}
          value={String(records.length)}
          sub="this period"
          icon={Hash}
        />
      </div>

      {/* Table card */}
      <Card className="overflow-hidden">
        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-3 border-b px-4 py-3 sm:px-5">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search ledger…"
              className="pl-9"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>

          <Select
            value={activeMonth || '__all__'}
            onValueChange={(v) => setParam('month', v === '__all__' ? '' : v)}
          >
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All · {year}</SelectItem>
              {months.map((m) => (
                <SelectItem key={m} value={m}>
                  {monthLabel(m)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={search.category || '__all__'}
            onValueChange={(v) => setParam('category', v === '__all__' ? '' : v)}
          >
            <SelectTrigger className="w-44">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <span className="ml-auto text-xs text-muted-foreground">
            {q.trim() && filteredRecords.length !== records.length
              ? `Showing ${filteredRecords.length} of ${records.length} entries`
              : `${records.length} entries`}
          </span>
        </div>

        {/* Table body */}
        {isLoading ? (
          <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
        ) : (
          <FinanceTable records={filteredRecords} onEdit={setEditRecord} />
        )}
      </Card>

      {editRecord && (
        <FinanceRecordDialog
          open
          onOpenChange={(v) => !v && setEditRecord(null)}
          editRecord={editRecord}
          onSuccess={() => setEditRecord(null)}
        />
      )}
    </div>
  );
}
